import { UserReportResponse } from '@zond/api-interfaces';
import { FileText } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Streamdown } from 'streamdown';

interface UserReportTextProps {
  data?: UserReportResponse;
  loading?: boolean;
}

export const UserReportText: React.FC<UserReportTextProps> = ({ data, loading }) => {
  const { t } = useTranslation('reports');
  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-2" />
            <div className="h-24 bg-gray-200 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  const mainReport = data?.processedDataPeriod?.data;
  const detailedReports = data?.detailedReports || [];
  const hasReports =
    (mainReport && mainReport.trim()) || detailedReports.some((report) => report.data && report.data.trim());

  return (
    <div className="h-full p-6 pt-2">
      {hasReports ? (
        <div className="space-y-8">
          {mainReport && mainReport.trim() ? (
            <Streamdown className="prose dark:prose-invert prose-slate prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-base prose-p:leading-7 prose-li:text-base prose-strong:text-primary prose-strong:font-semibold max-w-none">
              {mainReport}
            </Streamdown>
          ) : (
            detailedReports.map(
              (report) =>
                report.data &&
                report.data.trim() && (
                  <div key={report.id}>
                    <Streamdown className="prose dark:prose-invert prose-slate prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-base prose-p:leading-7 prose-li:text-base prose-strong:text-primary prose-strong:font-semibold max-w-none">
                      {report.data}
                    </Streamdown>
                  </div>
                )
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
            <FileText className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">{t('report.emptyTitle')}</h3>
          <p className="text-muted-foreground">{t('report.emptyDescription')}</p>
        </div>
      )}
    </div>
  );
};
